import React, { useEffect, useState } from 'react';
import { Provider, useDispatch } from 'react-redux';
import { store, AppDispatch } from './store';
import { initDB } from './utils/db';
import { runLastMigration } from './utils/migration';
import { AppRouter } from './AppRouter';
import ErrorBoundary from './components/ErrorBoundary';
import { badgeService } from './services/badgeService';
import { loadSettings } from './store/slices/uiSlice';

import './App.css';

const AppContent: React.FC = () => {
  const [isReady, setIsReady] = useState(false);
  const [initError, setInitError] = useState<Error | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // 1. IndexedDB 初期化 (スキーマ + 設定 + 統計の初期値投入)
        await initDB();

        // 2. localStorage からの最終移行 (一度きり)
        await runLastMigration();

        // 3. アプリ設定の読み込み (IndexedDB -> Redux)
        await dispatch(loadSettings());

        // 4. バッジ・統計サービスの初期化 (IndexedDB -> Memory Map)
        await badgeService.initialize();

        setIsReady(true);
      } catch (error) {
        console.error('[App] Initialization failed:', error);
        setInitError(error instanceof Error ? error : new Error(String(error)));
      }
    };

    initializeApp();
  }, [dispatch]);

  // 初期化エラーが発生した場合は、レンダリング時にthrowして ErrorBoundary に捕捉させる（安易なフォールバックの排除）
  if (initError) {
    throw initError;
  }

  if (!isReady) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>読み込み中...</p>
      </div>
    );
  }

  return <AppRouter />;
};

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <AppContent />
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
